import { useCallback, useEffect, useRef } from "react";

import { flushOfflineOrders, readQueue } from "@/lib/offlineSync";
import { requestDurableStorage } from "@/lib/offlineStore";
import { useAppDispatch, useAppSelector } from "@/store";
import {
  setDurability,
  setLastSync,
  setOfflineQueue,
  setOfflineSyncing,
  setOnline,
} from "@/store/slices/offlineOrdersSlice";
import { setSnackbar } from "@/store/slices/snackbarSlice";
import { relayOfflineOrderToNative } from "@/utils/webview";

/**
 * Drives the offline queue. PRODUCT CHANGE §2 (`DEVIATIONS.md`).
 *
 * Renders nothing and is mounted once in `_app`, beside `LiveOrdersBridge` and
 * `AuthBootstrap`, which is where this app already puts a headless effect that
 * has to outlive any single route.
 *
 * ## When a flush happens
 *
 * 1. **At boot**, after the queue is read off disk. A tab that was closed with
 *    orders queued sends them the moment it is opened again with a connection.
 * 2. **On the `online` event.** The browser's own signal, and the cheap one.
 * 3. **On a timer while anything is queued** — `online` is not reliable on its
 *    own. It fires for "this adapter has a link", not "the API is reachable",
 *    so a shop whose router is up and whose uplink is down never sees it. The
 *    poll is the backstop, and it stops the moment the queue is empty.
 * 4. **On demand**, from the banner's "Send now".
 *
 * `flushOfflineOrders` shares one in-flight promise, so all four can collide
 * without posting anything twice.
 */
const RETRY_INTERVAL_MS = 30000;

export default function OfflineOrdersBridge() {
  const dispatch = useAppDispatch();
  const queued = useAppSelector((state) => state.offlineOrders.orders.length);
  const syncing = useAppSelector((state) => state.offlineOrders.syncing);

  /** Guards the snackbar: only announce a flush that actually sent something. */
  const previousQueued = useRef(0);

  const flush = useCallback(async () => {
    dispatch(setOfflineSyncing(true));
    try {
      const outcome = await flushOfflineOrders();
      dispatch(setOfflineQueue(outcome.remaining));
      dispatch(setLastSync({ at: Date.now(), error: outcome.error ?? null }));
      if (outcome.confirmed > 0) {
        dispatch(
          setSnackbar({
            // `<confirmed>/<attempted>` is the POS's own wording, and it is the
            // wording rather than a bare "synced" because a PARTIAL batch has
            // to be visible: "8/10" is the only thing that tells an operator
            // two orders are still waiting.
            message: `${outcome.confirmed}/${outcome.attempted} offline order${
              outcome.attempted === 1 ? "" : "s"
            } sent 🎉`,
            severity: "success",
            anchorOrigin: { vertical: "top", horizontal: "center" },
          }),
        );
      }
    } finally {
      dispatch(setOfflineSyncing(false));
    }
  }, [dispatch]);

  // Boot: read the queue off disk, ask for durable storage, then try once.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [rows, durability] = await Promise.all([
        readQueue(),
        requestDurableStorage(),
      ]);
      if (cancelled) return;
      dispatch(setOfflineQueue(rows));
      rows.forEach((row) => {
        if (row.lan_relay_enabled) relayOfflineOrderToNative(row.document);
      });
      dispatch(setDurability(durability));
      dispatch(setOnline(navigator.onLine !== false));
      previousQueued.current = rows.length;
      if (rows.length > 0 && navigator.onLine !== false) void flush();
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, flush]);

  // The browser's connectivity events.
  useEffect(() => {
    const goOnline = () => {
      dispatch(setOnline(true));
      void flush();
    };
    const goOffline = () => dispatch(setOnline(false));
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, [dispatch, flush]);

  // The backstop poll, alive only while there is something to send.
  useEffect(() => {
    if (queued === 0 || syncing) return undefined;
    const timer = window.setInterval(() => {
      if (navigator.onLine !== false) void flush();
    }, RETRY_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [queued, syncing, flush]);

  return null;
}
